import { FC, useEffect, useState, ChangeEvent } from "react";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { getSurveySection } from "app/features/surveySlice";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { FormControlModel } from "types";
import { Button } from "@mui/material";
import FormComponentFactory from "lib/factory/FormComponentFactory";
import { ElementType } from "lib/enums/enums";
import StepContent from "../StepContent";
import { NO, YES } from "pages/SurveyForm/constants/uiControls";
import Select from "ui/molecules/Select";
import RadioGroupForm from "ui/molecules/RadioGroupForm";
import TextField from "ui/atoms/TextField";
import ErrorMessage from "ui/atoms/ErrorMessage";
import { questionIds } from "../../constants/initialValues";
import LanguageTable from "../LanguageTable";

type Props = {
  formik: any;
  sectionId: number;
};

type DropdownContainerProps = {
  isOtherLanguage: boolean;
};

export type LanguageModel = {
  anotherOption: string;
  personId?: string;
  questionID?: string;
  relatedAnswer: string;
  subSectionId: number;
  surveyTokenID?: string;
  updatedBy?: string;
  useCreation?: string;
  value: string;
};

const Step15: FC<Props> = ({ formik, sectionId }) => {
  const dispatch = useAppDispatch();
  const [languages, setLanguages] = useState<LanguageModel[]>([]);
  const [anotherOption, setAnotherOption] = useState<string>("");
  const isLanguage = formik.values.isOtherLanguage?.value === YES;
  const [language, setLanguage] = useState<string>("");
  const [level, setLevel] = useState<string>("");
  const [error, setError] = useState<string>("");
  const { surveySection } = useAppSelector((state) => state.survey) ?? [];
  const { questions = [] } = surveySection || {};
  const languageArray: any = questions[0]?.subQuestions[0]?.source ?? [];
  const languageLevelArray: any = questions[0]?.subQuestions[1]?.source ?? [];
  const languageListQuestionId = questionIds.step15.languageList || "";
  const otherLanguageQuestionId = languageArray[languageArray.length - 1]?.value ?? "";

  useEffect(() => {
    dispatch(getSurveySection(sectionId));
  }, []);

  useEffect(() => {
    if (formik.values.languageList) {
      setLanguages(formik.values.languageList);
    }
  }, [formik.values.languageList]);

  const addingLanguage = () => {
    if (!language) {
      setError("Debe seleccionar un idioma");
      return;
    }

    if (language === otherLanguageQuestionId && !anotherOption) {
      setError("Debe especificar el otro idioma");
      return;
    }

    if (!level) {
      setError("Debe seleccionar un nivel");
      return;
    }

    if (language || anotherOption) {
      const isLanguageOnTheList = languages.some((item) => item.value === language);
      if (isLanguageOnTheList) {
        const errorText = anotherOption
          ? "Solo se permite agregar un idioma que no este en la lista"
          : "El idioma ya esta en la lista";
        setError(errorText);
        return;
      }
      const languagesArray: LanguageModel[] = [
        ...languages,
        {
          value: language,
          anotherOption,
          relatedAnswer: level,
          subSectionId: languages.length + 1,
          questionID: languageListQuestionId,
        },
      ];
      formik.setFieldValue("languageList", languagesArray);
      setLanguages(languagesArray);
      setLanguage("");
      setLevel("");
      setAnotherOption("");
      setError("");
    }
  };

  const handleLanguageChange = (event: ChangeEvent<HTMLInputElement>) => {
    setLanguage(event.target.value);
    setError("");
    setAnotherOption("");
  };

  const handleLevelChange = (event: ChangeEvent<HTMLInputElement>) => {
    setLevel(event.target.value);
    setError("");
  };

  const handleOtherLanguageChange = (event: ChangeEvent<HTMLInputElement>) => {
    setAnotherOption(event.target.value);
    setError("");
  };

  const clearLanguages = (value: string) => {
    if (value === NO) {
      formik.setFieldValue("languageList", [{ value: "", subSectionId: 0, questionID: languageListQuestionId }]);
      setLanguage("");
      setLevel("");
      setAnotherOption("");
      setError("");
      return;
    }
    const languagesArrayFilterd = formik.values.languageList.filter((item: any) => item.value !== "");
    formik.setFieldValue("languageList", languagesArrayFilterd);
  };

  const dataModel: Array<FormControlModel> = [
    {
      key: 0,
      formik,
      visible: true,
      name: "computingKnowledge",
      questionData: questions[1],
      elementType: ElementType.Checkbox,
      label: "¿Qué manejo tiene de computadores?",
      checkboxStyle: {
        minWidth: "350px",
        margin: "0 1rem",
      },
    },
    {
      key: 1,
      formik,
      visible: true,
      isRanking: true,
      name: "knowledgeRanking",
      questionData: questions[2],
      elementType: ElementType.Checkbox,
      label:
        "¿Cuáles de las siguientes necesidades de formación o capacitación, considera las más prioritarias? \r\n(seleccione las tres opciones más relevantes/ importantes para usted)",
      checkboxStyle: {
        minWidth: "350px",
        margin: "0 1rem",
      },
    },
  ];

  return (
    <div>
      <Typography mb={2} variant='subtitle1' align='left'>
        Cuéntenos más de usted: formación
      </Typography>
      <StepContent>
        <RadioGroupForm
          formik={formik}
          name='isOtherLanguage'
          visible={true}
          label='¿Habla otro idioma?'
          onChangeCallback={clearLanguages}
          questionData={questions[0]}
        />

        {!!isLanguage && (
          <>
            <LanguageInfo>
              Use el boton <Strong>AGREGAR IDIOMA</Strong> para cargar uno o varios lenguajes a la lista.
            </LanguageInfo>
            <DropdownContainer isOtherLanguage={language === otherLanguageQuestionId}>
              <Select
                name='languages'
                label='Seleccione el idioma'
                onChange={handleLanguageChange}
                dataSource={languageArray}
                value={language}
                mapKey='value'
                mapValue='descripcion'
              />
              <SubContainer isOtherLanguage={language === otherLanguageQuestionId}>
                {language === otherLanguageQuestionId && (
                  <TextField
                    id='other-language-input'
                    label='Especifique otro idioma'
                    value={anotherOption}
                    onChange={handleOtherLanguageChange}
                  />
                )}
                <Select
                  name='languageLevel'
                  label='Seleccione el nivel'
                  onChange={handleLevelChange}
                  dataSource={languageLevelArray}
                  value={level}
                  mapKey='value'
                  mapValue='descripcion'
                />
              </SubContainer>
            </DropdownContainer>
            <ErrorMessage>{error || formik?.errors?.languageList}</ErrorMessage>
            <AddButtonWrapper>
              <Button variant='outlined' onClick={addingLanguage}>
                Agregar Idioma
              </Button>
            </AddButtonWrapper>
            <LanguageTable
              formik={formik}
              languages={languages}
              languageLevelArray={languageLevelArray}
              languageArray={languageArray}
              setLanguages={setLanguages}
              setError={setError}
            />
          </>
        )}
        {dataModel.map(FormComponentFactory)}
      </StepContent>
    </div>
  );
};

export default Step15;

const AddButtonWrapper = styled("div")`
  padding: 1rem 0;
  display: flex;
`;

const SubContainer = styled("div")<DropdownContainerProps>`
  width: 100%;
  display: flex;
  padding-top: ${({ isOtherLanguage }) => (isOtherLanguage ? "1rem" : "0")};
  flex-direction: ${({ isOtherLanguage }) => (isOtherLanguage ? "row" : "column")};
  @media (max-width: 500px) {
    flex-direction: column;
    padding-top: ${({ isOtherLanguage }) => (isOtherLanguage ? "0.25rem" : "0")};
  }
`;

const DropdownContainer = styled("div")<DropdownContainerProps>`
  display: flex;
  flex-direction: ${({ isOtherLanguage }) => (isOtherLanguage ? "column" : "row")};

  div:first-of-type {
    margin-right: 1rem;
  }

  @media (max-width: 500px) {
    flex-direction: column;
    .css-xztulr-MuiFormControl-root-MuiTextField-root {
      padding-bottom: 0.8rem;
    }
  }
`;

const Strong = styled("strong")`
  font-weight: bold;
`;

const LanguageInfo = styled("p")`
  font-style: italic;
  font-size: 0.8rem;
  padding-bottom: 1rem;
  color: #3f3f3f;
`;
